const parseHex = hex => {
  const normalized = hex.replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map(char => char + char)
          .join('')
      : normalized;

  if (full.length !== 6) return null;

  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map(value =>
      Math.max(0, Math.min(255, Math.round(value)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;

const rgbToHsl = ({ r, g, b }) => {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  let hue = 0;
  let saturation = 0;

  if (max !== min) {
    const delta = max - min;
    saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

    switch (max) {
      case red:
        hue = ((green - blue) / delta + (green < blue ? 6 : 0)) / 6;
        break;
      case green:
        hue = ((blue - red) / delta + 2) / 6;
        break;
      default:
        hue = ((red - green) / delta + 4) / 6;
        break;
    }
  }

  return { h: hue * 360, s: saturation * 100, l: lightness * 100 };
};

const hslToRgb = ({ h, s, l }) => {
  const hue = h / 360;
  const saturation = s / 100;
  const lightness = l / 100;

  if (saturation === 0) {
    const gray = lightness * 255;
    return { r: gray, g: gray, b: gray };
  }

  const hue2rgb = (p, q, t) => {
    let value = t;
    if (value < 0) value += 1;
    if (value > 1) value -= 1;
    if (value < 1 / 6) return p + (q - p) * 6 * value;
    if (value < 1 / 2) return q;
    if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6;
    return p;
  };

  const q =
    lightness < 0.5
      ? lightness * (1 + saturation)
      : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;

  return {
    r: hue2rgb(p, q, hue + 1 / 3) * 255,
    g: hue2rgb(p, q, hue) * 255,
    b: hue2rgb(p, q, hue - 1 / 3) * 255,
  };
};

const mixHex = (hex, targetHex, ratio) => {
  const source = parseHex(hex);
  const target = parseHex(targetHex);
  if (!source || !target) return hex;

  return rgbToHex({
    r: source.r * (1 - ratio) + target.r * ratio,
    g: source.g * (1 - ratio) + target.g * ratio,
    b: source.b * (1 - ratio) + target.b * ratio,
  });
};

const shiftHue = (hex, degrees) => {
  const rgb = parseHex(hex);
  if (!rgb) return hex;

  const hsl = rgbToHsl(rgb);
  hsl.h = (hsl.h + degrees + 360) % 360;
  hsl.s = Math.min(100, hsl.s * 0.92);
  hsl.l = Math.min(78, hsl.l * 1.08);

  return rgbToHex(hslToRgb(hsl));
};

// WCAG relative luminance (0 = black, 1 = white).
const relativeLuminance = hex => {
  const rgb = parseHex(hex);
  if (!rgb) return 0;

  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(value => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const LIGHT_FOREGROUND = '#fbf9ff';
const DARK_FOREGROUND = '#0a0a0a';

// Text colour for a brand-filled surface: whichever of near-white / near-black reads better,
// so light tenant colours (yellow, lime, sky) don't get white text.
const readableForeground = hex => {
  const luminance = relativeLuminance(hex);
  const contrastWithLight = (relativeLuminance(LIGHT_FOREGROUND) + 0.05) / (luminance + 0.05);
  const contrastWithDark = (luminance + 0.05) / (relativeLuminance(DARK_FOREGROUND) + 0.05);

  return contrastWithLight >= contrastWithDark ? LIGHT_FOREGROUND : DARK_FOREGROUND;
};

export const normalizeColorToHex = color => {
  if (!color || typeof color !== 'string') return null;

  const trimmed = color.trim();
  const hexCandidate = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;

  if (parseHex(hexCandidate)) {
    return hexCandidate.toLowerCase();
  }

  if (typeof document === 'undefined') return null;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) return null;

  context.fillStyle = '#000000';
  context.fillStyle = trimmed;

  const computed = context.fillStyle;

  if (typeof computed === 'string' && computed.startsWith('#')) {
    return computed.toLowerCase();
  }

  return null;
};

export const buildBrandPalette = primaryColor => {
  const hex = normalizeColorToHex(primaryColor);
  if (!hex) return null;

  const complement = shiftHue(hex, 145);

  return {
    primary: hex,
    opacity: `${hex}11`,
    light: mixHex(hex, '#ffffff', 0.42),
    soft: mixHex(hex, '#ffffff', 0.28),
    deep: mixHex(hex, '#000000', 0.18),
    complement,
    complementSoft: mixHex(complement, '#ffffff', 0.35),
    foreground: readableForeground(hex),
    // Dark mode lifts --primary 18% towards white (globals.css), so pick its text colour from that.
    foregroundDark: readableForeground(mixHex(hex, '#ffffff', 0.18)),
  };
};
